import React, { useState } from 'react';
import { dayjs } from '../support/helpers/dayjs.ts';
import { useSubscription } from '@meraki-internal/state';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { Field, FieldList } from '../components/field/Field.tsx';
import { TextInput } from '../components/field/TextInput.tsx';
import { AircraftStatus, FrameType, IAircraftWeight } from './FleetManagmentClient.ts';
import { AircraftFormState } from './AircraftFormState.ts';
import { SegmentInputGroup } from '../components/field/SegmentInputGroup.tsx';
import { AIRCRAFT_TYPE_LABELS, AIRCRAFT_STATUS_LABELS, AIRCRAFT_WEIGHT_LABELS } from './labels.ts';
import { IntegerInput } from '../components/field/IntegerInput.tsx';
import { DateInput } from '../components/field/DateInput.tsx';
import { SearchableSelectField } from '../components/field/SearchableSelectField.tsx';
import { RadioGroup } from '../components/field/RadioGroup.tsx';

export const AircraftForm: React.FC<{formRef: React.RefObject<HTMLFormElement | null>}> = ({ formRef }) => {
    const state = useInstance(AircraftFormState);
    useSubscription(() => state);

    const frameTypes: FrameType[] = ['rotary-wing', 'fixed-wing', 'other'];
    const statuses: AircraftStatus[] = ['active', 'grounded', 'archived'];
    const weights: IAircraftWeight[] = ['under-250g', '250g-55lb', 'over-55lb'];

    const [isOther, setIsOther] = useState(state.form?.make && state.form.make !== 'DJI');

    const onDJIOrOther = (value?: string) => {
        if (value === 'DJI') {
            setIsOther(false);
            state.change({ make: 'DJI', model: undefined });
        } else {
            setIsOther(true);
            state.change({ make: undefined, model: undefined });
        }
    };

    return (
        <form ref={formRef}>
            <FieldList>
                <Field
                    dataSet={{type: 'nickname'}}
                    label="Nickname"
                    required
                >
                    <TextInput
                        value={state.form?.nickname || ''}
                        autofocus={state.autoFocus()}
                        onChange={value => state.change({ nickname: value })}
                    />
                </Field>
                <Field
                    dataSet={{ type: 'frameType'}}
                    label="Drone Type"
                    stacked
                >
                    <SegmentInputGroup
                        value={state.form.frameType || 'rotary-wing'}
                        onChange={value => state.change({ frameType: value })}
                        options={frameTypes.map(value => ({ value, label: AIRCRAFT_TYPE_LABELS[value] }))}
                    />
                </Field>
                <Field
                    dataSet={{ type: 'status'}}
                    label="Drone Status"
                    stacked
                >
                    <SegmentInputGroup
                        value={state.form.status || 'active'}
                        onChange={value => state.change({ status: value })}
                        options={statuses.map(value => ({ value, label: AIRCRAFT_STATUS_LABELS[value] }))}
                    />
                </Field>
                <Field
                    dataSet={{type: 'djiOrOther'}}
                    label="Drone Make"
                    stacked
                    required
                >
                    <div style={{marginTop: 10, display: 'flex', gap: 20}}>
                        <RadioGroup
                            options={[
                                {label: 'DJI', value: 'DJI'},
                                {label: 'Other', value: 'Other'}
                            ]}
                            value={isOther ? 'Other' : state.form?.make === 'DJI' ? 'DJI' : undefined}
                            onChange={value => onDJIOrOther(value)}
                            required
                        />
                    </div>
                </Field>
                {isOther &&
                    <Field
                        dataSet={{type: 'make'}}
                        label="Other Make"
                        required
                    >
                        <TextInput
                            value={state.form?.make || ''}
                            onChange={make => state.change({ make, model: '' })}
                        />
                    </Field>
                }
                {(state.form?.make || isOther) &&
                    <Field
                        dataSet={{type: 'model'}}
                        label={state.form?.make === 'DJI' ? undefined : 'Drone Model'}
                        required
                    >
                        {state.form?.make === 'DJI' ? (
                            <SearchableSelectField
                                label="Drone Model"
                                placeholder="Select from list or type to search"
                                allowCustom
                                selected={state.form?.model ? {label: state.form.model, value: state.form.model} : undefined}
                                options={[
                                    'Agras T10',
                                    'Agras T30',
                                    'Agras T50',
                                    'Air 2S',
                                    'Air 3',
                                    'Air 3S',
                                    'Avata',
                                    'Avata 2',
                                    'Flip',
                                    'FPV',
                                    'Inspire 1',
                                    'Inspire 2',
                                    'Inspire 3',
                                    'Matrice 200',
                                    'Matrice 210 RTK',
                                    'Matrice 210',
                                    'Matrice 30',
                                    'Matrice 300 RTK',
                                    'Matrice 30T',
                                    'Matrice 350 RTK',
                                    'Matrice 4E',
                                    'Matrice 4T',
                                    'Mavic 2 Enterprise',
                                    'Mavic 2 Pro',
                                    'Mavic 2 Zoom',
                                    'Mavic 3',
                                    'Mavic 3 Cine',
                                    'Mavic 3 Classic',
                                    'Mavic 3 Enterprise',
                                    'Mavic 3 Pro',
                                    'Mavic 3 Thermal',
                                    'Mavic 4 Pro',
                                    'Mavic Air 2',
                                    'Mini 2',
                                    'Mini 2 SE',
                                    'Mini 3',
                                    'Mini 3 Pro',
                                    'Mini 4 Pro',
                                    'Mini 5 Pro',
                                    'Phantom 4 Pro V2.0',
                                    'Phantom 4 RTK',
                                ].map(m => ({label: m, value: m}))}
                                onChange={option => state.change({ model: option ? option.label : '' })}
                            />
                        ) : (
                            <TextInput
                                value={state.form?.model || ''}
                                onChange={value => state.change({ model: value })}
                            />
                        )}
                    </Field>
                }
                <Field
                    dataSet={{type: 'serial'}}
                    label="Serial Number"
                >
                    <TextInput
                        value={state.form?.serial || ''}
                        onChange={value => state.change({ serial: value })}
                    />
                </Field>
                <Field
                    dataSet={{type: 'value'}}
                    label="Drone Value ($)"
                >
                    <IntegerInput
                        value={state.form?.value}
                        onChange={value => state.change({ value: value })}
                    />
                </Field>
                <Field
                    dataSet={{ type: 'weight'}}
                    label="Drone Weight"
                    stacked
                >
                    <SegmentInputGroup
                        value={state.form.weight}
                        onChange={value => state.change({ weight: value })}
                        options={weights.map(value => ({ value, label: AIRCRAFT_WEIGHT_LABELS[value] }))}
                    />
                </Field>
                <Field
                    dataSet={{type: 'faaRegistrationNumber'}}
                    label="FAA Registration Number"
                >
                    <TextInput
                        value={state.form?.faaRegistrationNumber || ''}
                        onChange={value => state.change({ faaRegistrationNumber: value })}
                    />
                </Field>
                <Field
                    dataSet={{type: 'faaRegistrationDate'}}
                    label="FAA Registration Date"
                >
                    <DateInput
                        max={dayjs().format('YYYY-MM-DD')}
                        value={state.form.faaRegistrationDate}
                        onChange={value => state.change({ faaRegistrationDate: value })}
                    />
                </Field>
            </FieldList>
        </form>
    );
};
